/**
 * T-212: the plain-words line the Report Subscriptions inventory shows under
 * "Last attempt" — what happened to the latest scheduled send. `negative` is
 * shown in the destructive color (a send that did not fully go out); the rest
 * stay muted.
 */
import type { InventoryLastAttempt } from '@/lib/reportInventoryApi';

const REASONS: Record<string, string> = {
  no_data_in_window: 'no data for the period',
  no_units: 'no trailers in scope',
  no_recipients: 'no one to send to',
  no_sendable_recipients: 'no one to send to',
  no_date_range: 'no date range',
  owner_revoked: 'the owner lost access',
  subscription_disabled: 'the subscription was turned off',
  missed_send_time: 'its send time had already passed; the next one goes out on schedule',
  lease_expired: 'the send stopped three times',
  too_old_to_retry: 'the send stopped and was too old to retry',
  all_recipients_failed: 'no email went through',
  owner_check_failed: 'the owner could not be checked',
  too_large: 'the report was too large',
  render_service_not_configured: 'the report builder is not set up',
};

const reasonText = (reason: string | null) =>
  reason ? (REASONS[reason] ?? reason.replace(/_/g, ' ')) : 'unknown reason';

export function attemptLabel(attempt: InventoryLastAttempt | null | undefined): { text: string; tone: 'muted' | 'negative' } | null {
  if (!attempt) return null;
  switch (attempt.status) {
    case 'running': return { text: 'Sending…', tone: 'muted' };
    case 'sent': return { text: 'Sent', tone: 'muted' };
    case 'partial':
      return { text: `Partly sent (${attempt.recipients_sent ?? 0} of ${attempt.recipients_total ?? '?'})`, tone: 'negative' };
    case 'skipped': return { text: `Not sent: ${reasonText(attempt.reason)}`, tone: 'muted' };
    case 'stale_scope': return { text: 'Not sent: its scope no longer matches the company', tone: 'negative' };
    case 'failed': return { text: `Failed: ${reasonText(attempt.reason)}`, tone: 'negative' };
    default: return null;
  }
}
